import * as React from 'react'

import { StepAnimationWrapper } from '../components/step-animation-wrapper'
import { TeamOnboardingJb } from './team-onboarding-jb'

import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import {
  repoTransferInputSchema,
  repoTransferOutputSchema,
} from '~/app/api/teams/[team-id]/transfer-repository-request/input-schema'
import { usePersonalTeamAndRepos } from '~/hooks-data/use-personal-team-and-repos'

import { TeamOnboardingMembers, type Invite } from './team-onboarding-members'
import { TeamOnboardingRepoTransfer } from './team-onboarding-repo-transfer'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'

import { type Repository } from '@basehub/data/replicache/index'

export const TeamOnboarding: React.FC<{
  currentStep: Step
  prevStep: Step | null
  onStepChange: (currentStep: Step) => void
  onSkipSteps: (e?: React.MouseEvent, action?: 'push' | 'replace') => void
}> = ({ currentStep, prevStep, onStepChange, onSkipSteps }) => {
  const { team } = useSelectedTeam()
  const { team: personalTeam, repositories } = usePersonalTeamAndRepos()
  const [inviteList, setInvites] = React.useState<Array<Invite>>([])
  const [reposToTransfer, setReposToTransfer] = React.useState<
    Array<Repository['id']>
  >([])

  const { mutate: mutateRepoTransfer, isLoading } = useMutation(
    async ({
      repoIds,
      targetTeamId,
    }: {
      repoIds: Array<Repository['id']>
      targetTeamId: string
    }) => {
      const response = await fetch(
        `/api/teams/${personalTeam?.id}/transfer-repository-request`,
        {
          method: 'POST',
          body: JSON.stringify(
            repoTransferInputSchema.parse({
              repoIds,
              targetTeamId,
            })
          ),
        }
      )

      if (![200, 201].includes(response.status)) {
        const data = await response.json()
        console.error(data)
        const safeError = repoTransferOutputSchema.safeParse(data)
        if (safeError.success) {
          throw new Error(safeError.data.message)
        }
        throw new Error("Unknown error, couldn't transfer repository")
      }

      return response.json()
    }
  )

  const onFinish = () => {
    if (!team) return
    if (reposToTransfer.length) {
      mutateRepoTransfer(
        { repoIds: reposToTransfer, targetTeamId: team.id },
        {
          onSuccess: () => {
            onSkipSteps(undefined, 'replace')
            toast.success('Onboarding successful!')
          },
        }
      )
    } else {
      onSkipSteps(undefined, 'replace')
      toast.success('Onboarding successful!')
    }
  }

  return (
    <>
      {[currentStep, prevStep].includes('jb') && (
        <StepAnimationWrapper
          active={currentStep === 'jb'}
          direction={['jb', 'form'].includes(prevStep ?? 'jb') ? 'out' : 'in'}
        >
          <TeamOnboardingJb
            onSkip={onSkipSteps}
            onNextStep={() => {
              if (!repositories) return
              if (repositories.length > 0) onStepChange('repo-transfer')
              else onStepChange('invite-members')
            }}
          />
        </StepAnimationWrapper>
      )}
      {[currentStep, prevStep].includes('repo-transfer') && (
        <StepAnimationWrapper
          active={currentStep === 'repo-transfer'}
          direction={prevStep === 'invite-members' ? 'in' : 'out'}
        >
          <TeamOnboardingRepoTransfer
            focusInput={prevStep !== 'repo-transfer'}
            preselectedRepos={reposToTransfer}
            onSkip={onSkipSteps}
            onNextStep={(_, reposToTransfer) => {
              setReposToTransfer(reposToTransfer)
              onStepChange('invite-members')
            }}
          />
        </StepAnimationWrapper>
      )}

      {[currentStep, prevStep].includes('invite-members') && (
        <StepAnimationWrapper
          active={currentStep === 'invite-members'}
          direction={prevStep !== 'invite-members' ? 'out' : 'in'}
        >
          <TeamOnboardingMembers
            focusInput={prevStep !== 'invite-members'}
            onSkip={(e, inviteList) => {
              e.preventDefault()
              if (!repositories) return
              setInvites(inviteList)
              onStepChange(repositories.length > 0 ? 'repo-transfer' : 'jb')
            }}
            preselectedInvites={inviteList}
            onNextStep={onFinish}
            loading={isLoading}
          />
        </StepAnimationWrapper>
      )}
    </>
  )
}

export type Step =
  | 'alpha-form'
  | 'form'
  | 'jb'
  | 'repo-transfer'
  | 'invite-members'
export type OnboardingStep<T = object> = T & {
  onSkip: (e: React.MouseEvent) => void
  onNextStep: (e: React.MouseEvent) => void
}
